import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { 
  ArrowRight, Target, Users, TrendingUp, Cpu, Shield, Layers, BookOpen, Lightbulb, Network,
  Zap, Brain, Handshake, GraduationCap, Sparkles, CheckCircle, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BackNavigation from "@/components/BackNavigation";
import { useCMSContent } from "@/hooks/useCMSContent";
import ConstitutionalAuthoritySection from "@/components/wait/ConstitutionalAuthoritySection";
import WAITAdditionalDoctrine from "@/components/wait/WAITAdditionalDoctrine";

// Cinematic visual assets - 4K UHD Human-Centered (PROTECTED - DO NOT MODIFY)
const CINEMATIC_VISUALS = {
  heroVideo: "https://videos.pexels.com/video-files/3255275/3255275-uhd_2560_1440_25fps.mp4",
  heroPoster: "https://images.unsplash.com/photo-1578496479939-722d9dd1cc5b?w=1920&q=80",
  neural: "https://images.pexels.com/photos/8442147/pexels-photo-8442147.jpeg?w=1920",
  abstract: "https://images.pexels.com/photos/9574565/pexels-photo-9574565.jpeg?w=1920"
};

// System Architecture Diagram
const SYSTEM_ARCHITECTURE_DIAGRAM = "https://customer-assets.emergentagent.com/job_629d6fc3-18af-45e6-8912-76e3ef56678f/artifacts/8qd3p0bd_file_00000000a994722f8cd6bf78b8fea85f.png";

// Seven Forces Circular Diagram
const SEVEN_FORCES_DIAGRAM = "https://customer-assets.emergentagent.com/job_629d6fc3-18af-45e6-8912-76e3ef56678f/artifacts/1tvxcop2_Screenshot_20260323-214708_ChatGPT.png";

// Fallback content for CMS
const FALLBACK_CONTENT = {
  hero: {
    badge: "Foundational Doctrine",
    title: "WAIT Theory",
    subtitle: "Workforce AI Integration Theory",
    description: "The theoretical foundation behind workforce intelligence measurement. WAIT Theory defines the principles, indices, and frameworks that drive organizational transformation in the age of AI."
  }
};

/**
 * WAIT Theory Page
 * Foundational doctrine of the Zodiac Workforce Intelligence Institute
 * This is NOT a marketing page - it's the conceptual model that drives everything
 */
export default function WAITTheory() {
  // The 5 core dimensions of WAIT Theory
  const dimensions = [
    {
      icon: Users,
      title: "Workforce Trust",
      shortCode: "T",
      description: "The foundation of organizational performance. Measures leadership transparency, psychological safety, and collective confidence in organizational direction.",
      principle: "Trust enables capability. Without trust, no workforce transformation can succeed."
    },
    {
      icon: Target,
      title: "Alignment",
      shortCode: "A",
      description: "The degree to which individuals, teams, and systems are oriented toward shared strategic objectives with coordinated operations.",
      principle: "Alignment amplifies effort. Misalignment dissipates it."
    },
    {
      icon: Shield,
      title: "Resilience",
      shortCode: "R",
      description: "The capacity to adapt, recover, and transform in response to disruption, change, and evolving workforce demands.",
      principle: "Resilience sustains performance through uncertainty."
    },
    {
      icon: TrendingUp,
      title: "Readiness",
      shortCode: "R",
      description: "The preparedness of the workforce to perform, adapt, and grow. Encompasses skills, training effectiveness, and change adaptability.",
      principle: "Readiness precedes capability. Capability precedes performance."
    },
    {
      icon: Cpu,
      title: "AI Integration",
      shortCode: "I",
      description: "The organization's preparedness to integrate artificial intelligence into workforce operations, workflows, and decision-making.",
      principle: "AI integration is not optional. It is the defining capability of the emerging workforce."
    }
  ];

  // The theory application levels
  const applicationLevels = [
    {
      level: "Individual",
      lab: "Workforce Lab",
      focus: "Personal workforce intelligence development, skill alignment, and career pathway optimization",
      link: "/zodiac-lab/workforce"
    },
    {
      level: "Organizational",
      lab: "Organization Lab",
      focus: "Workforce diagnostics, alignment analysis, and AI integration strategy at the company level",
      link: "/zodiac-lab/organization"
    },
    {
      level: "Institutional",
      lab: "Institutional Lab",
      focus: "Sector-wide intelligence, policy frameworks, and benchmarking for workforce systems",
      link: "/zodiac-lab/institutional"
    }
  ];

  // The Seven Forces of Workforce Intelligence
  const sevenForces = [
    {
      number: 1,
      icon: Zap,
      title: "Capability Acceleration",
      description: "The ability to rapidly develop, upgrade, and apply skills in response to changing workforce demands and technological evolution.",
      color: "#E97451"
    },
    {
      number: 2,
      icon: Users,
      title: "Workforce Autonomy",
      description: "The capacity of individuals to operate with independence, decision-making ability, and adaptive responsibility within dynamic environments.",
      color: "#4CAF50"
    },
    {
      number: 3,
      icon: Handshake,
      title: "Trust & Alignment",
      description: "The degree to which individuals, teams, and systems are aligned in purpose, values, and execution, supported by mutual trust.",
      color: "#2196F3"
    },
    {
      number: 4,
      icon: GraduationCap,
      title: "Learning Transformation",
      description: "The shift from traditional learning to continuous, adaptive, and intelligence-driven development integrated into real-world application.",
      color: "#1E3A8A"
    },
    {
      number: 5,
      icon: Sparkles,
      title: "Opportunity Fluidity",
      description: "The ability to access, recognize, and convert opportunities into economic, professional, or organizational value.",
      color: "#9C27B0"
    },
    {
      number: 6,
      icon: CheckCircle,
      title: "Quality Discipline",
      description: "The consistency of standards, execution excellence, and adherence to systems that ensure reliability and performance.",
      color: "#F44336"
    },
    {
      number: 7,
      icon: Building2,
      title: "Institutional Coherence",
      description: "The alignment between policies, structures, and systems that enable sustainable workforce development at scale.",
      color: "#607D8B"
    }
  ];

  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  // Fetch CMS content with fallback
  const { content } = useCMSContent('wait-theory', FALLBACK_CONTENT);
  const hero = content.hero || FALLBACK_CONTENT.hero;
  
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.6;
      const handleLoaded = () => setVideoLoaded(true);
      video.addEventListener('loadeddata', handleLoaded);
      return () => video.removeEventListener('loadeddata', handleLoaded);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="wait-theory-page">
      {/* Back Navigation */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero Section - Cinematic Video Background */}
      <section className="relative overflow-hidden min-h-[70vh] flex items-center -mt-12">
        {/* Video Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ 
              backgroundImage: `url(${CINEMATIC_VISUALS.heroPoster})`,
              opacity: videoLoaded ? 0 : 1
            }}
          />
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            poster={CINEMATIC_VISUALS.heroPoster}
          >
            <source src={CINEMATIC_VISUALS.heroVideo} type="video/mp4" />
          </video>
          {/* Gradient Overlay - 30% opacity */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#991B1B]/35 via-[#7F1D1D]/30 to-[#991B1B]/25" />
          {/* Subtle pattern */}
          <div className="absolute inset-0 opacity-[0.05]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6" data-testid="wait-hero-badge">
              <Lightbulb className="w-4 h-4" />
              {hero.badge}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white" data-testid="wait-hero-title">
              {hero.title}
            </h1>
            <p className="text-xl sm:text-2xl font-semibold text-white/90 mb-6" data-testid="wait-hero-subtitle">
              {hero.subtitle}
            </p>
            <p className="text-base sm:text-lg text-white/80 leading-relaxed mb-8 max-w-3xl">
              WAIT Theory is the foundational framework of the Zodiac Workforce Intelligence Institute. 
              It provides a systematic model for understanding, measuring, and developing workforce intelligence 
              across individuals, organizations, and institutions in the age of artificial intelligence.
            </p>
            <div className="bg-white/10 backdrop-blur-sm border-l-4 border-white p-6 rounded-r-lg">
              <p className="text-white font-medium italic text-lg">
                "Beyond Credentials. Toward Measurable Workforce Intelligence."
              </p>
              <p className="text-white/70 text-sm mt-2">
                — Zodiac Institute Doctrine
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Constitutional Authority Edition — Live Publication Feature */}
      <ConstitutionalAuthoritySection />

      {/* Core Principle */}
      <section className="relative py-16 overflow-hidden">
        {/* Background texture layer */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${CINEMATIC_VISUALS.neural})`,
            opacity: 0.15
          }}
        />
        {/* Color overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/95 to-[#1E40AF]/95" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
              The Core Principle
            </h2>
            <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
              Zodiac does not merely teach courses. Zodiac develops <span className="font-bold">measurable workforce intelligence</span>. 
              WAIT Theory provides the scientific framework for assessing, developing, and aligning workforce capability 
              in an era defined by artificial intelligence transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Five Dimensions */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              The Five Dimensions of Workforce Intelligence
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              WAIT Theory defines five measurable dimensions that together comprise workforce intelligence.
            </p>
          </div>

          <div className="space-y-6">
            {dimensions.map((dimension, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="border-[#E5E7EB] hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                          <dimension.icon className="w-8 h-8 text-[#1E3A8A]" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="w-8 h-8 rounded-full bg-[#991B1B] text-white flex items-center justify-center font-bold text-sm">
                            {index + 1}
                          </span>
                          <h3 className="font-heading text-xl font-bold" style={{ color: '#991B1B' }}>
                            {dimension.title}
                          </h3>
                        </div>
                        <p className="text-[#1E3A8A] mb-4">{dimension.description}</p>
                        <p className="text-sm text-[#1E3A8A]/70 italic border-l-2 border-[#991B1B]/30 pl-4">
                          {dimension.principle}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Framework */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Theory to Application
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              WAIT Theory is operationalized through Zodiac Lab at three levels of application.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {applicationLevels.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-[#991B1B]/10 flex items-center justify-center mb-4">
                      <Layers className="w-6 h-6 text-[#991B1B]" />
                    </div>
                    <p className="text-sm text-[#1E3A8A]/60 font-medium mb-1">Level {index + 1}</p>
                    <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#991B1B' }}>
                      {level.level}
                    </h3>
                    <p className="text-sm font-semibold text-[#1E3A8A] mb-3">{level.lab}</p>
                    <p className="text-sm text-[#1E3A8A]/80 mb-4">{level.focus}</p>
                    <Link to={level.link}>
                      <Button variant="outline" size="sm" className="w-full border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white">
                        Explore {level.lab}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Circular Diagram - Core Model */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              The Workforce Intelligence Model
            </h2>
            <p className="text-base text-[#1E3A8A]/80 max-w-2xl mx-auto">
              A unified framework connecting all dimensions of workforce capability, development, and performance.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <img 
              src={SEVEN_FORCES_DIAGRAM}
              alt="Workforce Intelligence Model - Seven Forces surrounding the core: Capability Acceleration, Workforce Autonomy, Trust & Alignment, Learning Transformation, Opportunity Fluidity, Quality Discipline, and Institutional Coherence"
              className="w-full max-w-lg h-auto rounded-xl shadow-lg"
              data-testid="seven-forces-diagram"
            />
          </motion.div>
        </div>
      </section>

      {/* Seven Forces of Workforce Intelligence */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="seven-forces-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#991B1B' }}>
              The Seven Forces of Workforce Intelligence
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <p className="text-lg text-[#1E3A8A]">
                Workforce Intelligence is driven by seven interconnected forces that determine how individuals, 
                organizations, and institutions develop, align, and create value in the AI era.
              </p>
              <p className="text-base text-[#1E3A8A]/80">
                These forces operate as a dynamic system, not in isolation, shaping workforce readiness, 
                performance, and long-term capability.
              </p>
            </div>
          </motion.div>

          {/* Seven Forces Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {sevenForces.map((force, index) => (
              <motion.div
                key={force.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${force.color}15` }}
                      >
                        <force.icon className="w-6 h-6" style={{ color: force.color }} />
                      </div>
                      <div>
                        <span 
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold mb-2"
                          style={{ backgroundColor: force.color }}
                        >
                          {force.number}
                        </span>
                        <h3 className="font-heading text-lg font-bold" style={{ color: '#991B1B' }}>
                          {force.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-[#1E3A8A]/80 leading-relaxed">
                      {force.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-white border border-[#E5E7EB] rounded-xl p-6 sm:p-8 text-center max-w-4xl mx-auto"
          >
            <p className="text-[#1E3A8A] text-lg leading-relaxed">
              Together, these seven forces form the operational foundation of Workforce Intelligence, 
              enabling measurable alignment, adaptability, and value creation across all levels of the workforce system.
            </p>
          </motion.div>
        </div>
      </section>

      {/* System Architecture Diagram Section */}
      <section className="py-10 lg:py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <h2 className="font-heading text-xl sm:text-2xl font-bold mb-2" style={{ color: '#991B1B' }}>
              The Workforce Intelligence System Architecture
            </h2>
            <p className="text-sm text-[#1E3A8A]/80 max-w-xl mx-auto">
              This diagram illustrates how AI capability, workforce development, and measurement systems 
              are integrated within the Zodiac ecosystem.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-[#F8FAFC] rounded-xl border border-[#E5E7EB] p-3 sm:p-4 shadow-md overflow-hidden"
          >
            <img 
              src={SYSTEM_ARCHITECTURE_DIAGRAM}
              alt="Workforce Intelligence System Architecture - showing AI Observatory, Zodiac Lab System (Workforce Lab, Organization Lab, Institutional Lab), and Workforce Observatory with feedback loop"
              className="max-w-full h-auto rounded-lg mx-auto object-contain"
              style={{ maxHeight: '320px' }}
              data-testid="system-architecture-diagram"
            />
            <p className="text-xs text-center text-[#1E3A8A]/50 mt-2">
              Figure 1: Zodiac Workforce Intelligence System Architecture
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intelligence System */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              The Intelligence System
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Two observatories provide the measurement and intelligence infrastructure for WAIT Theory.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-[#E5E7EB] bg-gradient-to-br from-[#1E3A8A]/5 to-white">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6">
                    <Cpu className="w-7 h-7 text-[#1E3A8A]" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-3" style={{ color: '#991B1B' }}>
                    AI Observatory
                  </h3>
                  <p className="text-[#1E3A8A] mb-4">
                    Identifies, organizes, evaluates, and standardizes AI capabilities for workforce integration.
                  </p>
                  <ul className="space-y-2 text-sm text-[#1E3A8A]/80 mb-6">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#1E3A8A]"></div>
                      AI tool identification and registry
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#1E3A8A]"></div>
                      Capability evaluation and standardization
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#1E3A8A]"></div>
                      Lab integration support
                    </li>
                  </ul>
                  <Link to="/observatories/ai">
                    <Button className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                      Explore AI Observatory
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-[#E5E7EB] bg-gradient-to-br from-[#991B1B]/5 to-white">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-[#991B1B]/10 flex items-center justify-center mb-6">
                    <Network className="w-7 h-7 text-[#991B1B]" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-3" style={{ color: '#991B1B' }}>
                    Global Workforce Observatory
                  </h3>
                  <p className="text-[#1E3A8A] mb-4">
                    Measures workforce readiness, alignment, performance, and AI integration across the global workforce.
                  </p>
                  <ul className="space-y-2 text-sm text-[#1E3A8A]/80 mb-6">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#991B1B]"></div>
                      Workforce Intelligence Index (WII)
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#991B1B]"></div>
                      Benchmarking and reporting
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#991B1B]"></div>
                      Industry and regional analytics
                    </li>
                  </ul>
                  <Link to="/observatories/global">
                    <Button className="bg-[#991B1B] text-white hover:bg-[#7F1D1D]">
                      Explore Global Observatory
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Cinematic Doctrinal Sections */}
      <WAITAdditionalDoctrine />

      {/* CTA */}
      <section className="py-16 bg-[#1E3A8A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Begin Your Workforce Intelligence Journey
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Enter Zodiac Lab to apply WAIT Theory at the individual, organizational, or institutional level.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/zodiac-lab">
              <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-14 px-8">
                Enter Zodiac Lab
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/observatories/global/survey">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-8">
                Take the Diagnostic
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
